import React, { forwardRef } from 'react';
import { Divider, MenuItem, TextField, type TextFieldProps } from '@mui/material';

import { PLACE_OF_SERVICE_CODES } from '../../types';

type PlaceOfServiceSelectProps = Omit<TextFieldProps, 'select' | 'children'> & {
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

// Most commonly used POS codes
const COMMON_POS_CODES = ['02', '11', '12', '21', '22', '23'];

/**
 * Place of Service dropdown for CMS-1500 Box 24B
 * Shows common codes at top, then all others
 * Displays code + description in menu, only code when selected
 */
export const PlaceOfServiceSelect = forwardRef<HTMLDivElement, PlaceOfServiceSelectProps>(({
  value = '',
  size = 'small',
  sx,
  ...props
}, ref) => {
  // Custom render to show code + label when selected
  const renderValue = (selected: string) => {
    if (!selected) {
      // When compact (small), show "--" as placeholder; otherwise let the label float naturally
      return size === 'small'
        ? <span style={{ color: 'rgba(0, 0, 0, 0.6)' }}>--</span>
        : '';
    }
    const label = PLACE_OF_SERVICE_CODES[selected as keyof typeof PLACE_OF_SERVICE_CODES];
    return label ? `${selected} - ${label}` : selected;
  };

  const commonCodes = Object.entries(PLACE_OF_SERVICE_CODES)
    .filter(([code]) => COMMON_POS_CODES.includes(code))
    .sort((a, b) => COMMON_POS_CODES.indexOf(a[0]) - COMMON_POS_CODES.indexOf(b[0]));

  const otherCodes = Object.entries(PLACE_OF_SERVICE_CODES)
    .filter(([code]) => !COMMON_POS_CODES.includes(code))
    .sort((a, b) => a[0].localeCompare(b[0]));

  return (
    <TextField
      {...props}
      ref={ref}
      select
      value={value}
      size={size}
      slotProps={{
        select: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
          renderValue: renderValue as any,
          ...(size === 'small' && { displayEmpty: true }),
        },
      }}
      sx={{
        minWidth: '50px',
        ...(size === 'small' && {
          '& .MuiInputBase-input': {
            padding: '2px 4px',
            paddingRight: '20px',
            fontSize: '12px',
            textAlign: 'left',
          },
          '& .MuiOutlinedInput-root': {
            height: '24px',
          },
          '& .MuiSelect-icon': {
            right: '2px',
            top: '50%',
            transform: 'translateY(-50%)',
          },
        }),
        // eslint-disable-next-line @typescript-eslint/no-misused-spread
        ...(typeof sx === 'object' && !Array.isArray(sx) ? sx : {}),
      }}
    >
      <MenuItem value="">
        <em>None</em>
      </MenuItem>
      
      {/* Common codes section */}
      {commonCodes.map(([code, name]) => (
        <MenuItem 
          key={code} 
          value={code}
          sx={{ 
            fontSize: '13px',
            fontWeight: 500,
          }}
        >
          <span style={{ fontFamily: 'monospace', marginRight: '8px' }}>{code}</span>
          {name}
        </MenuItem>
      ))}
      
      <Divider sx={{ my: 0.5 }} />
      
      {/* Other codes section */}
      {otherCodes.map(([code, name]) => (
        <MenuItem 
          key={code} 
          value={code}
          sx={{ fontSize: '13px' }}
        >
          <span style={{ fontFamily: 'monospace', marginRight: '8px' }}>{code}</span>
          {name}
        </MenuItem>
      ))}
    </TextField>
  );
});

PlaceOfServiceSelect.displayName = 'PlaceOfServiceSelect';
